/**
 * BOM Editor Page
 * Two-panel layout: left = BOM lines (hierarchical tree), right = product search
 * Top: header info (bomCode, description, type, output, version, dates)
 */

import type { AddBomLineDTO, BomLine, BomType } from '@/api/manufacturing';
import { addBomLine, removeBomLine } from '@/api/manufacturing';
import { getProducts, type Product } from '@/api/products';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useBom, useCreateBom, useUpdateBom } from '@/hooks/use-manufacturing';
import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ─── Constants ──────────────────────────────────────────────────────────────

const BOM_TYPES: BomType[] = ['ASSEMBLY', 'KIT', 'REFURBISHMENT', 'CUSTOM'];

// ─── Form State ─────────────────────────────────────────────────────────────

interface BomForm {
  bomCode: string;
  description: string;
  bomType: BomType;
  outputProductId: string;
  outputProductName: string;
  outputQuantity: string;
  validFrom: string;
  validUntil: string;
}

const INITIAL_FORM: BomForm = {
  bomCode: '',
  description: '',
  bomType: 'ASSEMBLY',
  outputProductId: '',
  outputProductName: '',
  outputQuantity: '1',
  validFrom: '',
  validUntil: '',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Build a flat-to-tree structure from BOM lines */
function buildLineTree(lines: BomLine[]): BomLine[] {
  const map = new Map<string, BomLine>();
  const roots: BomLine[] = [];

  for (const line of lines) {
    map.set(line.id, { ...line, children: [] });
  }

  for (const line of lines) {
    const node = map.get(line.id);
    if (!node) continue;
    if (line.parentLineId) {
      const parent = map.get(line.parentLineId);
      if (parent) {
        if (!parent.children) parent.children = [];
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  }

  return roots;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function BomEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const isEditMode = Boolean(id);

  // Existing BOM data (for edit)
  const { bom: existingBom, isLoading: bomLoading } = useBom(id);

  // Mutations
  const { mutate: doCreate, isLoading: createLoading } = useCreateBom();
  const { mutate: doUpdate, isLoading: updateLoading } = useUpdateBom();

  // Form state
  const [form, setForm] = useState<BomForm>(INITIAL_FORM);
  const [lines, setLines] = useState<BomLine[]>([]);

  // Syncing guard to prevent double-save
  const [isSyncing, setIsSyncing] = useState(false);

  // Product search state (for adding components)
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Output product search state
  const [outputSearch, setOutputSearch] = useState('');
  const [outputResults, setOutputResults] = useState<Product[]>([]);
  const [outputSearchLoading, setOutputSearchLoading] = useState(false);
  const [showOutputSearch, setShowOutputSearch] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (existingBom) {
      setForm({
        bomCode: existingBom.bomCode,
        description: existingBom.description ?? '',
        bomType: existingBom.bomType,
        outputProductId: existingBom.outputProductId,
        outputProductName: existingBom.outputProductName,
        outputQuantity: existingBom.outputQuantity.toString(),
        validFrom: existingBom.validFrom?.split('T')[0] ?? '',
        validUntil: existingBom.validUntil?.split('T')[0] ?? '',
      });
      setLines(existingBom.lines ?? []);
    }
  }, [existingBom]);

  // Component product search handler
  const handleProductSearch = useCallback(async () => {
    if (!productSearch.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await getProducts({ search: productSearch, pageSize: 20 });
      setSearchResults(response.data);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [productSearch]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      if (!controller.signal.aborted) {
        void handleProductSearch();
      }
    }, 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [handleProductSearch]);

  // Output product search handler
  const handleOutputSearch = useCallback(async () => {
    if (!outputSearch.trim()) {
      setOutputResults([]);
      return;
    }
    setOutputSearchLoading(true);
    try {
      const response = await getProducts({ search: outputSearch, pageSize: 10 });
      setOutputResults(response.data);
    } catch {
      setOutputResults([]);
    } finally {
      setOutputSearchLoading(false);
    }
  }, [outputSearch]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      if (!controller.signal.aborted) {
        void handleOutputSearch();
      }
    }, 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [handleOutputSearch]);

  // Update form field
  const setField = <K extends keyof BomForm>(key: K, value: BomForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Select output product
  const handleSelectOutputProduct = (product: Product) => {
    setField('outputProductId', product.id);
    setField('outputProductName', product.name);
    setShowOutputSearch(false);
    setOutputSearch('');
    setOutputResults([]);
  };

  // Add component product to lines
  const handleAddProduct = (product: Product) => {
    const alreadyAdded = lines.some(line => line.productId === product.id);
    if (alreadyAdded) return;

    const newLine: BomLine = {
      id: `temp-${Date.now()}`,
      bomId: id ?? '',
      productId: product.id,
      productName: product.name,
      productSku: product.sku,
      quantity: 1,
      uom: 'db',
      scrapRate: 0,
      optional: false,
      sortOrder: lines.length,
    };
    setLines(prev => [...prev, newLine]);
  };

  // Remove line
  const handleRemoveLine = (lineId: string) => {
    setLines(prev => prev.filter(l => l.id !== lineId));
  };

  // Update line quantity
  const handleUpdateLineQty = (lineId: string, qty: number) => {
    setLines(prev =>
      prev.map(l => (l.id === lineId ? { ...l, quantity: Math.max(0.01, qty) } : l))
    );
  };

  // Update line scrap rate
  const handleUpdateLineScrapRate = (lineId: string, rate: number) => {
    setLines(prev =>
      prev.map(l => (l.id === lineId ? { ...l, scrapRate: Math.min(100, Math.max(0, rate)) } : l))
    );
  };

  // Toggle optional
  const handleToggleOptional = (lineId: string) => {
    setLines(prev => prev.map(l => (l.id === lineId ? { ...l, optional: !l.optional } : l)));
  };

  // Move line (reorder) — only swap among siblings with same parentLineId
  const handleMoveLine = (lineId: string, direction: 'up' | 'down') => {
    setLines(prev => {
      const line = prev.find(l => l.id === lineId);
      if (!line) return prev;
      const siblings = prev.filter(l => l.parentLineId === line.parentLineId);
      const siblingIdx = siblings.findIndex(l => l.id === lineId);
      const swapIdx = direction === 'up' ? siblingIdx - 1 : siblingIdx + 1;
      if (swapIdx < 0 || swapIdx >= siblings.length) return prev;
      const swapLine = siblings[swapIdx];
      if (!swapLine) return prev;
      const newLines = [...prev];
      const aIdx = newLines.findIndex(l => l.id === lineId);
      const bIdx = newLines.findIndex(l => l.id === swapLine.id);
      [newLines[aIdx], newLines[bIdx]] = [newLines[bIdx]!, newLines[aIdx]!];
      return newLines;
    });
  };

  // Build tree for display
  const lineTree = useMemo(() => buildLineTree(lines), [lines]);

  // Save handler
  const handleSave = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      if (isEditMode && id) {
        await doUpdate(id, {
          description: form.description || undefined,
          bomType: form.bomType,
          outputQuantity: parseFloat(form.outputQuantity) || 1,
          validFrom: form.validFrom || undefined,
          validUntil: form.validUntil || undefined,
        });

        // Sync lines: remove old, add new
        const existingLineIds = new Set((existingBom?.lines ?? []).map(l => l.id));
        const currentLineIds = new Set(lines.map(l => l.id));

        for (const existingLine of existingBom?.lines ?? []) {
          if (!currentLineIds.has(existingLine.id)) {
            await removeBomLine(id, existingLine.id);
          }
        }

        for (const line of lines) {
          if (!existingLineIds.has(line.id) || line.id.startsWith('temp-')) {
            const dto: AddBomLineDTO = {
              productId: line.productId,
              quantity: line.quantity,
              uom: line.uom,
              scrapRate: line.scrapRate,
              optional: line.optional,
              sortOrder: line.sortOrder,
              parentLineId: line.parentLineId,
            };
            await addBomLine(id, dto);
          }
        }

        navigate(`/manufacturing/bom/${id}`);
      } else {
        await doCreate({
          bomCode: form.bomCode,
          description: form.description || undefined,
          bomType: form.bomType,
          outputProductId: form.outputProductId,
          outputQuantity: parseFloat(form.outputQuantity) || 1,
          validFrom: form.validFrom || undefined,
          validUntil: form.validUntil || undefined,
        });
        navigate('/manufacturing/bom');
      }
    } catch {
      // Error is handled in hooks
    } finally {
      setIsSyncing(false);
    }
  };

  const isSaving = createLoading || updateLoading;

  if (bomLoading && id) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/manufacturing/bom')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {isEditMode ? t('manufacturing.bom.editBom') : t('manufacturing.bom.createNew')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('manufacturing.bom.editorSubtitle')}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/manufacturing/bom')}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={isSaving || isSyncing || !form.bomCode || !form.outputProductId}
              className="bg-primary hover:bg-primary/90"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Basic Info */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              {t('manufacturing.bom.basicInfo')}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('manufacturing.bom.bomCode')} *
                </label>
                <Input
                  value={form.bomCode}
                  onChange={e => setField('bomCode', e.target.value)}
                  placeholder="BOM-001"
                  disabled={isEditMode}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('common.type')}
                </label>
                <select
                  value={form.bomType}
                  onChange={e => setField('bomType', e.target.value as BomType)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-foreground dark:border-slate-600 dark:bg-slate-800"
                >
                  {BOM_TYPES.map(type => (
                    <option key={type} value={type}>
                      {t(`manufacturing.bomType.${type.toLowerCase()}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('manufacturing.bom.outputQuantity')}
                </label>
                <Input
                  type="number"
                  min={1}
                  value={form.outputQuantity}
                  onChange={e => setField('outputQuantity', e.target.value)}
                />
              </div>
              <div className="relative">
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('manufacturing.bom.outputProduct')} *
                </label>
                {form.outputProductId ? (
                  <div className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
                    <span className="flex-1 truncate text-foreground">
                      {form.outputProductName}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setField('outputProductId', '');
                        setField('outputProductName', '');
                        setShowOutputSearch(true);
                      }}
                    >
                      {t('common.remove')}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Input
                      value={outputSearch}
                      onChange={e => {
                        setOutputSearch(e.target.value);
                        setShowOutputSearch(true);
                      }}
                      onFocus={() => setShowOutputSearch(true)}
                      placeholder={t('manufacturing.bom.searchOutputProduct')}
                    />
                    {showOutputSearch && outputResults.length > 0 && (
                      <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800">
                        {outputResults.map(product => (
                          <button
                            key={product.id}
                            onClick={() => handleSelectOutputProduct(product)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-700"
                          >
                            <span className="truncate font-medium text-foreground">
                              {product.name}
                            </span>
                            <span className="text-muted-foreground">{product.sku}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {showOutputSearch && outputSearchLoading && (
                      <div className="absolute z-10 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm text-muted-foreground shadow-lg dark:border-slate-600 dark:bg-slate-800">
                        {t('common.loading')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('common.description')}
                </label>
                <textarea
                  value={form.description}
                  onChange={e => setField('description', e.target.value)}
                  placeholder={t('manufacturing.bom.descriptionPlaceholder')}
                  rows={2}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-foreground dark:border-slate-600 dark:bg-slate-800"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('manufacturing.bom.validFrom')}
                </label>
                <Input
                  type="date"
                  value={form.validFrom}
                  onChange={e => setField('validFrom', e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('manufacturing.bom.validUntil')}
                </label>
                <Input
                  type="date"
                  value={form.validUntil}
                  onChange={e => setField('validUntil', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Two-Panel Layout: BOM Lines + Product Search */}
        <div className="mb-6 grid gap-6 lg:grid-cols-2">
          {/* Left: BOM Lines */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="mb-4 text-lg font-semibold text-foreground">
                {t('manufacturing.bom.components')} ({lines.length})
              </h2>
              {lines.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  {t('manufacturing.bom.noLines')}
                </p>
              ) : (
                <div className="space-y-3">
                  {lineTree.map((line, idx) => (
                    <BomLineRow
                      key={line.id}
                      line={line}
                      depth={0}
                      idx={idx}
                      totalCount={lineTree.length}
                      onRemove={handleRemoveLine}
                      onUpdateQty={handleUpdateLineQty}
                      onUpdateScrapRate={handleUpdateLineScrapRate}
                      onToggleOptional={handleToggleOptional}
                      onMove={handleMoveLine}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Product Search */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="mb-4 text-lg font-semibold text-foreground">
                {t('manufacturing.bom.addComponent')}
              </h2>
              <Input
                placeholder={t('manufacturing.bom.searchComponentPlaceholder')}
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                className="mb-4"
              />

              {searchLoading && (
                <p className="py-4 text-center text-muted-foreground">{t('common.loading')}</p>
              )}

              {!searchLoading && productSearch && searchResults.length === 0 && (
                <p className="py-4 text-center text-muted-foreground">
                  {t('manufacturing.bom.noSearchResults')}
                </p>
              )}

              <div className="max-h-[500px] space-y-2 overflow-y-auto">
                {searchResults.map(product => {
                  const alreadyAdded = lines.some(l => l.productId === product.id);
                  return (
                    <div
                      key={product.id}
                      className={`flex items-center justify-between rounded-lg border p-3 dark:border-slate-700 ${
                        alreadyAdded
                          ? 'opacity-50'
                          : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50'
                      }`}
                      onClick={() => !alreadyAdded && handleAddProduct(product)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-foreground">{product.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {product.sku}
                          {product.brand ? ` - ${product.brand}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {alreadyAdded ? (
                          <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {t('manufacturing.bom.alreadyAdded')}
                          </Badge>
                        ) : (
                          <Button variant="ghost" size="sm">
                            + {t('common.add')}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

// ─── BOM Line Row ───────────────────────────────────────────────────────────

interface BomLineRowProps {
  line: BomLine;
  depth: number;
  idx: number;
  totalCount: number;
  onRemove: (id: string) => void;
  onUpdateQty: (id: string, qty: number) => void;
  onUpdateScrapRate: (id: string, rate: number) => void;
  onToggleOptional: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  t: (key: string, params?: Record<string, string>) => string;
}

function BomLineRow({
  line,
  depth,
  idx,
  totalCount,
  onRemove,
  onUpdateQty,
  onUpdateScrapRate,
  onToggleOptional,
  onMove,
  t,
}: BomLineRowProps) {
  return (
    <>
      <div
        className="flex items-center gap-3 rounded-lg border p-3 dark:border-slate-700"
        style={{ marginLeft: depth * 24 }}
      >
        {/* Sort controls */}
        <div className="flex flex-col gap-0.5">
          <button
            onClick={() => onMove(line.id, 'up')}
            disabled={idx === 0}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
            aria-label={t('manufacturing.bom.moveUp')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          </button>
          <button
            onClick={() => onMove(line.id, 'down')}
            disabled={idx === totalCount - 1}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
            aria-label={t('manufacturing.bom.moveDown')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {/* Item info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-foreground">{line.productName}</span>
            {line.optional && (
              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                {t('manufacturing.bom.optional')}
              </Badge>
            )}
          </div>
          {line.productSku && (
            <p className="mt-0.5 text-xs text-muted-foreground">{line.productSku}</p>
          )}
        </div>

        {/* Quantity */}
        <div className="w-20">
          <Input
            type="number"
            min={0.01}
            step={0.01}
            value={line.quantity}
            onChange={e => onUpdateQty(line.id, parseFloat(e.target.value) || 1)}
            className="text-center"
          />
        </div>

        {/* UOM */}
        <span className="w-8 text-center text-xs text-muted-foreground">{line.uom}</span>

        {/* Scrap rate */}
        <div className="flex w-20 items-center gap-1">
          <Input
            type="number"
            min={0}
            max={100}
            value={line.scrapRate}
            onChange={e => onUpdateScrapRate(line.id, parseFloat(e.target.value) || 0)}
            className="text-center"
          />
          <span className="text-xs text-muted-foreground">%</span>
        </div>

        {/* Optional toggle */}
        <button
          onClick={() => onToggleOptional(line.id)}
          className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
            line.optional
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
              : 'bg-gray-100 text-muted-foreground dark:bg-gray-700'
          }`}
          title={t('manufacturing.bom.toggleOptional')}
        >
          {line.optional ? t('manufacturing.bom.optional') : t('manufacturing.bom.required')}
        </button>

        {/* Remove */}
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700 dark:text-red-400"
          onClick={() => onRemove(line.id)}
        >
          {t('common.remove')}
        </Button>
      </div>

      {/* Render children recursively */}
      {line.children?.map((child, childIdx) => (
        <BomLineRow
          key={child.id}
          line={child}
          depth={depth + 1}
          idx={childIdx}
          totalCount={line.children?.length ?? 0}
          onRemove={onRemove}
          onUpdateQty={onUpdateQty}
          onUpdateScrapRate={onUpdateScrapRate}
          onToggleOptional={onToggleOptional}
          onMove={onMove}
          t={t}
        />
      ))}
    </>
  );
}
